/**
 * M-06 スタッフ・権限。利用者と給与マスタを管理する。
 */

import { useState } from 'react';
import { endpoints } from '../../api/endpoints.js';
import type { Employee, PayrollMasterView, Role } from '../../api/types.js';
import { useMe } from '../../auth/context.js';
import { Card, EmptyState, ErrorMessage, Loading, Notice } from '../../components/ui.js';
import { useAsync } from '../../lib/useAsync.js';

const ROLE_LABEL: Record<Role, string> = {
  staff: 'スタッフ',
  approver: '承認上長',
  admin: '管理部',
};

interface EmployeeForm {
  id: string | null;
  employeeId: number | null;
  name: string;
  email: string;
  department: string;
  role: Role;
  approvalGroupId: string | null;
  hireDate: string | null;
  active: boolean;
  excludedFromAttendance: boolean;
}

const EMPTY: EmployeeForm = {
  id: null,
  employeeId: null,
  name: '',
  email: '',
  department: '',
  role: 'staff',
  approvalGroupId: null,
  hireDate: null,
  active: true,
  excludedFromAttendance: false,
};

export function EmployeesPage(): React.JSX.Element {
  const me = useMe();
  const employees = useAsync(() => endpoints.employees(), []);
  const groups = useAsync(() => endpoints.groups(), []);
  const [form, setForm] = useState<EmployeeForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [filters, setFilters] = useState({ department: '', group: '', role: '' });

  const items = (employees.data?.items ?? []).filter(
    (employee) =>
      (filters.department === '' || employee.department === filters.department) &&
      (filters.group === '' || (employee.approvalGroupName ?? '未割当') === filters.group) &&
      (filters.role === '' || employee.role === filters.role),
  );
  const departments = [...new Set((employees.data?.items ?? []).map((item) => item.department))];
  const groupNames = [
    ...new Set((employees.data?.items ?? []).map((item) => item.approvalGroupName ?? '未割当')),
  ];

  const save = async (): Promise<void> => {
    if (!form) return;
    setBusy(true);
    setActionError(null);
    try {
      const payload = {
        employeeId: form.employeeId ?? undefined,
        name: form.name,
        email: form.email,
        department: form.department,
        role: form.role,
        approvalGroupId: form.approvalGroupId,
        hireDate: form.hireDate,
        active: form.active,
        excludedFromAttendance: form.excludedFromAttendance,
      };
      if (form.id === null) await endpoints.createEmployee(payload as Partial<Employee>);
      else await endpoints.updateEmployee(form.id, payload as Partial<Employee>);
      setForm(null);
      employees.reload();
      groups.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(false);
    }
  };

  if (employees.loading && !employees.data) return <Loading />;
  if (employees.error) return <ErrorMessage error={employees.error} />;

  return (
    <>
      <Notice tone="info" title="所属部門と承認グループは別の設定です">
        組織変更があっても承認関係を維持できるよう、承認上長は承認グループ側で設定します。
      </Notice>

      <ErrorMessage error={actionError} />

      <Card
        title={`利用者 ${items.length}名`}
        action={
          <div className="flex flex-wrap gap-2">
            <select
              className="input w-auto"
              value={filters.department}
              onChange={(event) => setFilters({ ...filters, department: event.target.value })}
              aria-label="部門で絞り込む"
            >
              <option value="">すべての部門</option>
              {departments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
            <select
              className="input w-auto"
              value={filters.group}
              onChange={(event) => setFilters({ ...filters, group: event.target.value })}
              aria-label="承認グループで絞り込む"
            >
              <option value="">すべてのグループ</option>
              {groupNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="input w-auto"
              value={filters.role}
              onChange={(event) => setFilters({ ...filters, role: event.target.value })}
              aria-label="権限で絞り込む"
            >
              <option value="">すべての権限</option>
              {(['staff', 'approver', 'admin'] as Role[]).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABEL[role]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setForm({ ...EMPTY })}
            >
              利用者を追加
            </button>
          </div>
        }
      >
        {items.length === 0 ? (
          <EmptyState message="該当する利用者はいません。" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>社員ID</th>
                  <th>氏名</th>
                  <th>メール</th>
                  <th>所属部門</th>
                  <th>承認グループ</th>
                  <th>承認上長</th>
                  <th>権限</th>
                  <th>在籍</th>
                  <th>勤怠提出</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.employeeId}</td>
                    <td className="font-semibold">{employee.name}</td>
                    <td className="text-ink-muted">{employee.email}</td>
                    <td>{employee.department}</td>
                    <td>{employee.approvalGroupName ?? '未割当'}</td>
                    <td className="text-ink-muted">{employee.approverName ?? '—'}</td>
                    <td>
                      <span className="pill pill--info">{ROLE_LABEL[employee.role]}</span>
                    </td>
                    <td>
                      <span className={`pill ${employee.active ? 'pill--ok' : 'pill--muted'}`}>
                        {employee.active ? '在籍' : '無効'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pill ${employee.excludedFromAttendance ? 'pill--muted' : 'pill--info'}`}
                      >
                        {employee.excludedFromAttendance ? '対象外' : '対象'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() =>
                          setForm({
                            id: employee.id,
                            employeeId: employee.employeeId,
                            name: employee.name,
                            email: employee.email,
                            department: employee.department,
                            role: employee.role,
                            approvalGroupId: employee.approvalGroupId,
                            hireDate: employee.hireDate,
                            active: employee.active,
                            excludedFromAttendance: employee.excludedFromAttendance,
                          })
                        }
                      >
                        編集
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {form && (
        <Card title={form.id === null ? '利用者の追加' : '利用者の編集'}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="field-label">社員ID</span>
              <input
                className="input"
                type="number"
                min={1}
                value={form.employeeId ?? ''}
                disabled={form.id !== null}
                onChange={(event) =>
                  setForm({
                    ...form,
                    employeeId: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              />
            </label>
            <label className="block">
              <span className="field-label">氏名</span>
              <input
                className="input"
                type="text"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="field-label">メールアドレス</span>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="field-label">所属部門</span>
              <input
                className="input"
                type="text"
                value={form.department}
                onChange={(event) => setForm({ ...form, department: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="field-label">承認グループ</span>
              <select
                className="input"
                value={form.approvalGroupId ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    approvalGroupId: event.target.value === '' ? null : event.target.value,
                  })
                }
              >
                <option value="">未割当</option>
                {(groups.data?.items ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="field-label">権限</span>
              <select
                className="input"
                value={form.role}
                disabled={form.id === me.employee.id}
                onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
              >
                {(['staff', 'approver', 'admin'] as Role[]).map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABEL[role]}
                  </option>
                ))}
              </select>
              {form.id === me.employee.id && (
                <span className="field-hint">自分自身の管理権限は変更できません</span>
              )}
            </label>
            <label className="block">
              <span className="field-label">入社日</span>
              <input
                className="input"
                type="date"
                value={form.hireDate ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    hireDate: event.target.value === '' ? null : event.target.value,
                  })
                }
              />
              <span className="field-hint">入社日より前の日報は集計対象外になります</span>
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => setForm({ ...form, active: event.target.checked })}
              />
              在籍中（外すとログインできなくなります）
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.excludedFromAttendance}
                onChange={(event) =>
                  setForm({ ...form, excludedFromAttendance: event.target.checked })
                }
              />
              勤怠提出の対象外（役員など。未入力・未承認に数えず、月次確定の条件からも外れます）
            </label>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void save()}
            >
              保存する
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
              取消
            </button>
          </div>
        </Card>
      )}

      {/* 利用者を追加・変更したら給与マスタの一覧も読み直す（新しい利用者の行が出ないままにならないように） */}
      <PayrollMasterSection version={employees.data} />
    </>
  );
}

/** 給与マスタ。管理部のみが参照でき、閲覧そのものが監査ログに残る */
function PayrollMasterSection({ version }: { version: unknown }): React.JSX.Element {
  const state = useAsync(() => endpoints.payrollMasters(), [version]);
  const [edit, setEdit] = useState<PayrollMasterView | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const save = async (): Promise<void> => {
    if (!edit) return;
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.updatePayrollMaster(edit.employeeId, {
        baseSalary: edit.baseSalary,
        positionAllowance: edit.positionAllowance,
        managerAllowance: edit.managerAllowance,
        fixedOvertimeAllowance: edit.fixedOvertimeAllowance,
        mealAllowanceUnit: edit.mealAllowanceUnit,
      });
      setEdit(null);
      state.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="給与マスタ（管理部のみ）"
      description="閲覧・変更はすべて監査ログに記録されます。金額が0円の間は勤怠集計専用として運用します。"
    >
      <ErrorMessage error={state.error} />
      <ErrorMessage error={actionError} />

      {state.data && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>社員ID</th>
                <th>氏名</th>
                <th className="text-right">基本給</th>
                <th className="text-right">職位手当</th>
                <th className="text-right">管理職手当</th>
                <th className="text-right">固定残業手当</th>
                <th className="text-right">食事手当単価</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.data.items.map((master) => (
                <tr key={master.employeeId}>
                  <td>{master.employeeNumber}</td>
                  <td className="font-semibold">{master.name}</td>
                  {(
                    [
                      'baseSalary',
                      'positionAllowance',
                      'managerAllowance',
                      'fixedOvertimeAllowance',
                      'mealAllowanceUnit',
                    ] as const
                  ).map((field) => (
                    <td key={field} className="text-right">
                      {edit?.employeeId === master.employeeId ? (
                        <input
                          className="input w-28 text-right"
                          type="number"
                          min={0}
                          value={edit[field]}
                          onChange={(event) =>
                            setEdit({ ...edit, [field]: Number(event.target.value) })
                          }
                        />
                      ) : (
                        master[field].toLocaleString('ja-JP')
                      )}
                    </td>
                  ))}
                  <td className="whitespace-nowrap">
                    {edit?.employeeId === master.employeeId ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          disabled={busy}
                          onClick={() => void save()}
                        >
                          保存
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setEdit(null)}
                        >
                          取消
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => setEdit({ ...master })}
                      >
                        編集
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
